import * as React from 'react';
import { Link } from 'react-router-dom';

import styled from '@emotion/styled';
import { Button, Flex, Text } from '@opendoor/bricks/core';
import { media } from '@opendoor/bricks/theme/novo';
import { ChevronLeft } from '@opendoor/cinderblocks/growth/novo/icons/ChevronLeft';

import { ContentContainer } from 'src/components/layouts/PageLayout';
import { useAppSelector } from 'src/store/hooks';
import { getCurrentSlug } from 'src/store/router/selectors';
import { getPreviousPath } from 'src/store/sellerFlow/selectors';

// TODO - bricks - figure this hover color out - it does not match any color in novo
// also bricks has a linear gradient as the background image that seems unneeded these days?
export const NovoButton = styled(Button)`
  ${media.smallerThan.LG} {
    padding: 14px 18px;
    min-height: 48px;
  }

  /**
   * the disabled state is momentarily set while loading is true,
   * these styles fix the text flashing and button shrinking that
   * can be seen when there is an extended loading time
   */
  &:disabled {
    background-color: rgb(0, 64, 230);
    color: #fff;
    transform: none;
  }

  &:hover {
    background-color: #0036c3;
  }
`;

const SkipButton = ({ onClickHandler, loading, buttonText }: NavButtonProps) => {
  const currentSlug = useAppSelector(getCurrentSlug);

  let skipButtonText = 'Skip';
  if (buttonText) {
    skipButtonText = buttonText;
  }

  return (
    <Button
      aria-label={null}
      analyticsName={`reception-fe-${currentSlug}-skip`}
      type="button"
      onClick={onClickHandler}
      loading={loading}
      variant="secondary"
      id="navigation-layout-skip"
    >
      {skipButtonText}
    </Button>
  );
};

const NextButton = ({ onClickHandler, loading, buttonText }: NavButtonProps) => {
  const currentSlug = useAppSelector(getCurrentSlug);
  const atEnd = useAppSelector((state) => state.flow_ng.atEnd);

  let nextButtonText = atEnd ? 'Submit & Finish' : 'Next';
  if (buttonText) {
    nextButtonText = buttonText;
  }

  return (
    <NovoButton
      variant="primary"
      onClick={onClickHandler}
      loading={loading}
      width="auto"
      minWidth={['100px', null, null, '115px']}
      border="1.5px solid"
      borderColor="brand50"
      analyticsName={`reception-fe-${currentSlug}-${buttonText}`}
      trackImpression={true}
      aria-label={null}
      id="navigation-layout-next"
    >
      {nextButtonText}
    </NovoButton>
  );
};

export const PreviousLink = () => {
  const previousPath = useAppSelector(getPreviousPath);
  if (previousPath) {
    return (
      <Link to={previousPath} aria-label="Previous Page">
        <Text color="warmgrey900">
          <ChevronLeft />
        </Text>
      </Link>
    );
  }
  return null;
};

type NavButtonProps = {
  buttonText: string;
  loading?: boolean;
  onClickHandler: () => void;
};

type NavigationProps = {
  nextButton?: NavButtonProps;
  skipButton?: NavButtonProps;
};

export const NavigationContainer: React.FC<React.PropsWithChildren> = ({ children }) => {
  return (
    <ContentContainer>
      <Flex
        flexDirection="row-reverse"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        margin="auto"
        maxWidth={['520px', null, '1440px']}
        pt={[5, null, null, 6]}
        pb={[5, null, null, 6]}
      >
        {children}
      </Flex>
    </ContentContainer>
  );
};

export const NavigationCTAContainer: React.FC<React.PropsWithChildren> = ({ children }) => {
  return (
    <Flex flexDirection="row" gap="16px">
      {children}
    </Flex>
  );
};

/**
 *
 * TODO: Refactor Navigation.tsx to use this component
 */
const NavigationLayout: React.FC<NavigationProps> = ({ nextButton, skipButton }) => {
  // todo - need to make it so 922+ can be the final full width+padding
  return (
    <NavigationContainer>
      <NavigationCTAContainer>
        {skipButton && (
          <SkipButton
            buttonText={skipButton.buttonText}
            onClickHandler={skipButton.onClickHandler}
            loading={skipButton.loading}
          />
        )}
        {nextButton ? (
          <NextButton
            buttonText={nextButton.buttonText}
            onClickHandler={nextButton.onClickHandler}
            loading={nextButton.loading}
          />
        ) : (
          // we still render a button, but we make it not visible, this makes
          // it so the bottom bar maintains the same height as having a button
          // present
          <Button
            analyticsName="reception-fe-hidden-next-button"
            aria-label={null}
            onClick={() => {}}
            visibility="hidden"
          >
            Next
          </Button>
        )}
      </NavigationCTAContainer>
      <PreviousLink />
    </NavigationContainer>
  );
};

export default NavigationLayout;
